import React from 'react';
import { useTenantUserInfo } from '../../context/userInfoProvider';

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
  gap: 12,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 13,
  opacity: 0.8,
};

const sectionHeader: React.CSSProperties = {
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 1,
  opacity: 0.75,
  marginBottom: 6,
};

const pill: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 10px',
  borderRadius: 999,
  backgroundColor: 'rgba(255,255,255,0.08)',
  fontSize: 11,
  letterSpacing: 0.7,
  textTransform: 'uppercase',
  opacity: 0.9,
};

const facilityCard: React.CSSProperties = {
  borderRadius: 10,
  padding: 14,
  backgroundColor: 'rgba(7, 5, 16, 0.85)',
  border: '1px solid rgba(164, 151, 255, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const facilityNameStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
};

const facilityAddressStyle: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.9,
};

const planCard: React.CSSProperties = {
  borderRadius: 10,
  padding: 14,
  background:
    'linear-gradient(135deg, rgba(123,31,162,0.95), rgba(91,63,166,0.98))',
  marginTop: 12,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 8,
};

const planItemLabel: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  opacity: 0.85,
  marginBottom: 2,
};

const planItemValue: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 500,
};

const priceTag: React.CSSProperties = {
  alignSelf: 'flex-start',
  justifySelf: 'flex-start',
  padding: '6px 12px',
  borderRadius: 999,
  backgroundColor: 'rgba(12,10,28,0.85)',
  fontSize: 14,
  fontWeight: 600,
};

const divider: React.CSSProperties = {
  height: 1,
  background:
    'linear-gradient(to right, rgba(255,255,255,0.18), rgba(255,255,255,0.02))',
  margin: '20px 0 16px',
};

const cancelHeader: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  marginBottom: 4,
};

const cancelText: React.CSSProperties = {
  fontSize: 13,
  opacity: 0.9,
  lineHeight: 1.5,
};

const TenantPlanPage: React.FC = () => {
  const {
    facilityName,
    facilityAddress,
    size,
    minStay,
    typeOfPlan,
    price,
  } = useTenantUserInfo();

  const hasFacility = Boolean(facilityName || facilityAddress);

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerRow}>
          <div>
            <h1 style={titleStyle}>Your storage plan</h1>
            <div style={subtitleStyle}>
              View the facility and plan details for your current Bins storage.
            </div>
          </div>
          <div style={pill}>Active plan</div>
        </div>

        {/* Facility section */}
        <div style={{ marginBottom: 18 }}>
          <div style={sectionHeader}>Facility</div>
          <div style={facilityCard}>
            <div style={facilityNameStyle}>
              {facilityName || 'Facility not set'}
            </div>
            <div style={facilityAddressStyle}>
              {facilityAddress || 'Your facility details will appear here once your account is fully set up.'}
            </div>
          </div>
        </div>

        {/* Plan section */}
        <div>
          <div style={sectionHeader}>Plan details</div>
          <div style={planCard}>
            <div>
              <div style={planItemLabel}>Size</div>
              <div style={planItemValue}>{size || 'N/A'}</div>
            </div>
            <div>
              <div style={planItemLabel}>Minimum stay</div>
              <div style={planItemValue}>{minStay || 'N/A'}</div>
            </div>
            {typeOfPlan && (
              <div>
                <div style={planItemLabel}>Plan type</div>
                <div style={planItemValue}>{typeOfPlan}</div>
              </div>
            )}
            <div style={priceTag}>{price || 'Price not available'}</div>
          </div>
        </div>

        {/* Divider + cancellation section */}
        <div style={divider} />

        <div>
          <div style={cancelHeader}>Need to change or cancel?</div>
          <div style={cancelText}>
            We&apos;re happy to help you adjust your plan, switch facilities, or
            cancel service. Just email us at{' '}
            <a
              href="mailto:contact@bins-storage.com"
              style={{ color: '#C7B6FF', textDecoration: 'none' }}
            >
              contact@bins-storage.com
            </a>
            {hasFacility
              ? ' and include your facility name so we can locate your account quickly.'
              : '.'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantPlanPage;
